import * as React from 'react';
import { Checkbox as MatCheckbox, FormControl, FormLabel, FormControlLabel, FormGroup, FormHelperText, SxProps, Theme } from '@mui/material';
import { List } from '../List';
import { ListItem } from '../ListItem';
import { keygen } from '../../utils';

type CheckboxColor = 'primary' | 'secondary' | 'default';
type MarginType = 'dense' | 'normal' | 'none';
type Orientation = 'vertical' | 'horizontal';

// TODO: Must add unique identifier per checkbox group/
interface CheckboxGroup {
  label?: string;
  helperText?: string;
  items: CheckboxItem[];
}

interface CheckboxItem {
  color?: CheckboxColor;
  description: string;
  id: string;
  isChecked: boolean;
  isEnabled: boolean;
}

export interface _DeprecatedCheckboxProps {
  checkboxGroup: CheckboxGroup;
  checked?: boolean;
  disabled?: boolean;
  disableRipple?: boolean;
  onChange: (id: string) => void;
  required?: boolean;
  margin?: MarginType;
  fullWidth?: boolean;
  noFormControl?: boolean;
  orientation?: Orientation;
}

const styles: Record<string, SxProps<Theme>> = {
  checkboxListItemWrapper: {
    padding: 0,
    '&:active': {
      backgroundColor: 'transparent'
    }
  },
  horizontalCheckboxListItemWrapper: {
    display: 'inline'
  }
};

const _DeprecatedCheckbox: React.FunctionComponent<_DeprecatedCheckboxProps> = ({
  noFormControl = false,
  checkboxGroup: { label, helperText, items },
  onChange,
  fullWidth,
  orientation,
  ...rest
}: _DeprecatedCheckboxProps) => {
  const checkboxKey = `checkbox-id-${keygen()}`;
  const checkboxIteratorKey = 'checkbox-key-';
  const { checkboxListItemWrapper, horizontalCheckboxListItemWrapper } = styles;

  return (
    <React.Fragment>
      {noFormControl ? (
        items.map(({ isChecked, id, isEnabled, color, description }: CheckboxItem) => (
          <MatCheckbox
            key={`${checkboxIteratorKey}${id}`}
            checked={isChecked}
            value={id}
            inputProps={{ 'aria-label': description }}
            disabled={!isEnabled}
            disableRipple
            onChange={onChange && (event => onChange(event.currentTarget.value))}
            color={color as CheckboxColor}
          />
        ))
      ) : (
        <FormControl fullWidth={fullWidth} {...rest}>
          <FormLabel id={checkboxKey} className="Checkbox-FormControl-Label">
            {label}
          </FormLabel>
          <FormGroup role="group" aria-labelledby={checkboxKey}>
            <List disablePadding>
              {items.map(({ isChecked, id, isEnabled, color, description }: CheckboxItem, index) => (
                <ListItem
                  key={`${checkboxIteratorKey}${id}`}
                  dense
                  disableGutters
                  sx={
                    {
                      ...checkboxListItemWrapper,
                      ...(orientation === 'horizontal' ? horizontalCheckboxListItemWrapper : null)
                    } as SxProps<Theme>
                  }
                >
                  <FormControlLabel
                    control={
                      <MatCheckbox
                        checked={isChecked}
                        value={id}
                        size="small"
                        id={`${checkboxKey}-${index}`}
                        disabled={!isEnabled}
                        disableRipple
                        onChange={onChange && (event => onChange(event.currentTarget.value))}
                        color={color as CheckboxColor}
                      />
                    }
                    label={description}
                    key={id}
                  />
                </ListItem>
              ))}
            </List>
          </FormGroup>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      )}
    </React.Fragment>
  );
};

_DeprecatedCheckbox.defaultProps = {
  fullWidth: true
};

_DeprecatedCheckbox.displayName = '_DeprecatedCheckbox';

export { _DeprecatedCheckbox };
